import {useUpdateUserDiffViewPreferenceMutation} from '@github-ui/diff-view-settings/hooks/mutations/use-update-user-diff-view-preference-mutation'
import {
  useUserDiffViewSettingsData,
  useSplitViewPreference,
  useWhitespacePreference,
} from '@github-ui/diff-view-settings/page-data/payloads/diff-view-settings'
import {CommentsPreference} from '@github-ui/diff-view-settings/types'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {GlobalCommands} from '@github-ui/ui-commands'
import {useHideFooter} from '@github-ui/use-hide-footer'
import {Dialog, SplitPageLayout} from '@primer/react'
import {clsx} from 'clsx'
import {useCallback, useEffect, useMemo, useRef} from 'react'
import {FileFilter, type FileFilterHandle} from './diff-filtering/FileFilter'
import {PR_FILE_TREE_ID, PullRequestFileTree} from './file-tree/PullRequestFileTree'
import treeStyles from './file-tree/PullRequestFileTree.module.css'
import {PageLimitsBanner} from './PageLimitsBanner'
import {LivePullRequestFilesToolbar} from './toolbar/PullRequestFilesToolbar'
import {useCodeowners} from '../page-data/loaders/use-codeowners-data'
import type {Codeowners} from '../page-data/payloads/codeowners'
import type {FilesRoutePayload} from '../page-data/payloads/files'
import styles from './DiffComparisonViewer.module.css'
import {PullRequestStoreProvider} from '../stores/PullRequestStore'
import {useRouteHeaderData} from '../hooks/use-route-header-data'
import {
  useFilteredDiffSummaries,
  useDiffSummaryCount,
  useDiffFilterActions,
  useCodeownersActions,
  useDiffFilterState,
  useCanAccessChangeGroups,
  useSettingsActions,
  useFilterResultsAnnouncement,
} from '../stores/selectors'
import type {DiffFilterState} from '../stores/slices/diff-filter'
import {PageDataContextProvider} from '@github-ui/pull-request-page-data-tooling/page-data-context'
import {useDiffComparisonData} from '../page-data/loaders/use-diff-comparison-data'
import {BasicDiffComparisonViewToolbar} from './toolbar/BasicDiffComparisonViewToolbar'
import {useTreePane, DIFF_COMPARISON_VIEWER_CONTAINER_ID} from '../hooks/use-tree-pane'
import {useAutoGenerateChangeGroups} from '../hooks/use-auto-generate-change-groups'
import {EntireDiffLoadingSkeleton} from './progressive-diff/EntireDiffLoadingSkeleton'
import {LoadingSkeleton} from '@github-ui/skeleton/LoadingSkeleton'
import {PullRequestDiffsList} from './PullRequestDiffsList'
import {PullRequestVirtualizedDiffsList} from './PullRequestVirtualizedDiffsList'
import {PullRequestCommitHeader} from './PullRequestCommitHeader'
import {useHashNavigation} from '../hooks/use-hash-navigation'
import {SingleFileModeDiffEntryNavigator} from './SingleFileModeDiffEntryNavigator'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {PullRequestAnalyticsStoreProvider} from '../stores/PullRequestsAnalyticsStore'
import {useFileTreeWidthSync, MIN_WIDTH, DEFAULT_WIDTH, MAX_WIDTH, STORAGE_KEY} from '../hooks/use-file-tree-width-sync'
import {LargeDiffOptimizationsBanner} from './LargeDiffOptimizationsBanner'

export type ConfigOpts = {
  forceUnifiedView: boolean
  useChangeGroups: boolean
  useInlineComments: boolean
  useEditOptions: boolean
  useFileTree: boolean
  useAdvancedToolbar: boolean
  useBasicToolbar: boolean
  userCanComment: boolean
  treeCollapsedByDefault: boolean
  treeEnableInlineCompactMode: boolean
}

/**
 * This type enforces that only one of `useAdvancedToolbar` or `useBasicToolbar` can be true at a time.
 * If both are false or undefined, then no toolbar will be shown.
 */
type DiffComparisonToolbarUnion =
  | {useAdvancedToolbar: true; useBasicToolbar?: false}
  | {useAdvancedToolbar?: false; useBasicToolbar: true}
  | {useAdvancedToolbar?: false; useBasicToolbar?: false}

export type DiffComparisonViewerProps = {
  basePath: string
  forceUnifiedView?: boolean
  useChangeGroups?: boolean
  useInlineComments?: boolean
  useEditOptions?: boolean
  useFileTree?: boolean
  userCanComment?: boolean
  treeCollapsedByDefault?: boolean
  /**
   * Enable inline compact mode for the file tree on compact viewports (< 1012px container width & > 768px screen)
   * When enabled, the file tree will replace the main content and will be shown inline instead of as an overlay.
   */
  treeEnableInlineCompactMode?: boolean
} & DiffComparisonToolbarUnion &
  DiffComparisonViewerDataSource

// Union type to enforce that either initialData is provided, or headSha (and optionally baseSha) is provided
// This is to ensure that the component has enough information to fetch the necessary data if initialData is not provided
type DiffComparisonViewerDataSource = LoadDataFromInitialData | LoadDataFromShas

type LoadDataFromInitialData = {
  initialData: FilesRoutePayload
  headSha?: never
  baseSha?: never
}

type LoadDataFromShas = {
  // If both head and base sha are present, the component will
  // grab diffs for that comparison instead of the pull request's base comparison.
  headSha: string
  baseSha?: string
  initialData?: never
}
export type DiffComparisonViewerState = FilesRoutePayload & {
  configOpts: ConfigOpts
}

/**
 * Renders the DiffComparisonViewer with some options in what to render that are unique for the consumer.
 *
 * The component takes optional values that it will use to determining rendering of specific functionality
 * @initialData: FilesRoutePayload - If present, the component will use this data to render the DiffComparisonViewer.
 * @useInlineComments: boolean - Will enable inline comments displaying if a user is able to comment on lines or files.
 * @useFileTree: boolean - Will render the Diff File Tree.
 * @useAdvancedToolbar: boolean - Will render the Toolbar with advanced PRs features like review state, commit selector, etc.
 * @useBasicToolbar: boolean - Will render a simplified PRs-agnostic Toolbar
 * @basePath: string - The base path for the pull request. This is required so that the TSQ cache is unique to each PR
 * @headSha: string - If present without baseSha, the component will fetch diffs for the pull request's base comparison up to this sha.
 * @baseSha: string - If present with headSha, the component will fetch diffs for that comparison instead of the pull request's base comparison.
 *                    NOTE: do not pass in the base SHA the pull request is based off of - this will cause the request to fail. Just pass in the
 *                    head SHA by itself instead.
 * @forceUnifiedView: boolean - If true, will force the diff view to be unified. Defaults to false/undefined.
 *
 * This component also exposes two custom events: `DiffAddCommentButtonClickedEvent` and `DiffSelectedLineEvent`.
 * these events are fired when a user clicks the "Add comment" + button on a line, or selects a line or range of lines in the diff viewer.
 *
 * the events both send detail with the following shape:
 * endLineNumber: number - the last line number in the selected range or that the comment is being made on.
 * endOrientation: 'left' | 'right' - the side of the diff the endLineNumber is on. (left is base/original, right is head/changed)
 * filePath: string - the file path of the file the line(s) are in.
 * startLineNumber: number | undefined - the first line number in the selected range. undefined if the comment is on a single line or a single line is selected
 * startOrientation: 'left' | 'right' | undefined - the side of the startLineNumber. Undefined if startLineNumber is undefined.
 *
 * Usage:
 *
 * 1. With initial data from FilesRoutePayload
 * ```
 *  function Some Component() {
 *    const routeData = useRoutePayload<FilesRoutePayload>()
 *
 *    // This example will render with commenting, simple toolbar and file tree
 *    <DiffComparisonViewer.Container>
 *      <DiffComparisonViewer.Viewer
 *        useCommenting
 *        useFileTree
 *        useBasicToolbar
 *        initialData={routeData}
 *      />
 *    </DiffComparisonViewer.Container>
 *  }
 * ```
 *  * 2. With no initial data and allowing DiffComparisonViewer to fetch Pull Request, Comparison (Diffs), and Markers info.
 * ```
 *  function Some Component() {
 *
 *    // This example will only renders diff comparisons and simple toolbar
 *    <DiffComparisonViewer.Container>
 *      <DiffComparisonViewer.Viewer
 *        useBasicToolbar
 *        basePath={/:owner/:repo/pull/:pull_number}
 *        headSha={commitHeadSha}
 *        baseSha={commitBaseSha}
 *      />
 *   </DiffComparisonViewer.Container>
 *  }
 * ```
 */
function BaseDiffComparisonViewer({
  basePath,
  baseSha,
  forceUnifiedView,
  headSha,
  initialData,
  treeCollapsedByDefault,
  treeEnableInlineCompactMode,
  userCanComment,
  useChangeGroups,
  useInlineComments,
  useEditOptions,
  useFileTree,
  useAdvancedToolbar,
  useBasicToolbar,
}: DiffComparisonViewerProps) {
  const configOpts = useMemo<ConfigOpts>(
    () => ({
      useChangeGroups: useChangeGroups ?? false,
      useInlineComments: useInlineComments ?? false,
      useEditOptions: useEditOptions ?? false,
      useFileTree: useFileTree ?? false,
      useAdvancedToolbar: useAdvancedToolbar ?? false,
      useBasicToolbar: useBasicToolbar ?? false,
      userCanComment: userCanComment ?? true,
      forceUnifiedView: forceUnifiedView ?? false,
      treeCollapsedByDefault: treeCollapsedByDefault ?? false,
      treeEnableInlineCompactMode: treeEnableInlineCompactMode ?? false,
    }),
    [
      useChangeGroups,
      useInlineComments,
      useEditOptions,
      useFileTree,
      useAdvancedToolbar,
      useBasicToolbar,
      userCanComment,
      forceUnifiedView,
      treeCollapsedByDefault,
      treeEnableInlineCompactMode,
    ],
  )

  // Get SHA values from either props OR initial data, not a combination of both
  // The union types ensure only one pattern is used at a time
  const normalizedHeadSha = initialData ? initialData.pullRequest.comparison.headOid : headSha
  const normalizedBaseSha = initialData ? initialData.pullRequest.comparison.baseOid : baseSha

  if (!normalizedHeadSha) {
    throw new Error('DiffComparisonViewer: headSha is required if initialData is not provided')
  }

  const {data: fetchedData, isLoading} = useDiffComparisonData({
    basePath,
    headSha: normalizedHeadSha,
    baseSha: normalizedBaseSha,
    shouldFetch: !initialData, // only fetch if initialData is not provided
  })

  const diffComparisonData = initialData ?? fetchedData

  const isDiffVirtualizationFlagWithoutMinThresholdEnabled = isFeatureEnabled(
    'pull_request_files_virtualization_without_min_threshold',
  )
  const virtualizationEnabled =
    (isDiffVirtualizationFlagWithoutMinThresholdEnabled || diffComparisonData?.virtualizeDiffEntries) ?? false

  return (
    <>
      {/* operating under the assumption that if ANY props are passed in, they are all passed in */}
      {diffComparisonData && !isLoading ? (
        <PageDataContextProvider basePageDataUrl={diffComparisonData.pullRequest.pathName}>
          <PullRequestAnalyticsStoreProvider
            initData={{
              diffVersion: 'v2',
              virtualizationEnabled,
            }}
          >
            <PullRequestStoreProvider initData={diffComparisonData} configOpts={configOpts}>
              <InternalDiffComparisonViewer {...diffComparisonData} configOpts={configOpts} />
            </PullRequestStoreProvider>
          </PullRequestAnalyticsStoreProvider>
        </PageDataContextProvider>
      ) : (
        <div className="width-full">
          <div className={styles.SkeletonWrapper}>
            <div className={styles.SkeletonIconWrapper}>
              <LoadingSkeleton width="lg" height="lg" variant="rounded" />
            </div>
          </div>
          <div className="d-flex flex-column gap-3 tmp-p-3">
            <EntireDiffLoadingSkeleton lines={4} />
            <EntireDiffLoadingSkeleton lines={8} />
            <EntireDiffLoadingSkeleton lines={5} />
          </div>
        </div>
      )}
    </>
  )
}

function InternalDiffComparisonViewer({
  codeowners,
  commit,
  commits,
  configOpts,
  diffSummaries: initialDiffSummaries,
  fileFilter,
  pullRequest,
  repository,
  pageLimits,
  urls,
  user,
  userNotices,
  isSingleFileMode,
  virtualizeDiffEntries,
}: FilesRoutePayload & {configOpts: ConfigOpts}) {
  useHideFooter(true)

  // Enable hash navigation for browser back/forward
  useHashNavigation()

  /*
   * Viewer settings
   */
  const viewSettingsData = useViewerSettings(user)

  const isDiffVirtualizationFlagWithoutMinThresholdEnabled = isFeatureEnabled(
    'pull_request_files_virtualization_without_min_threshold',
  )

  const {mutate: updateUserDiffViewPreference} = useUpdateUserDiffViewPreferenceMutation({
    onSuccess: () => {},
    onError: () => {},
  })
  const {data: settingsData} = useUserDiffViewSettingsData(viewSettingsData)
  const handleSwapDiffViewCommentCollapsePreference = useCallback(() => {
    updateUserDiffViewPreference({
      commentsPreference:
        settingsData?.commentsPreference === CommentsPreference.Visible
          ? CommentsPreference.Collapsed
          : CommentsPreference.Visible,
    })
  }, [settingsData?.commentsPreference, updateUserDiffViewPreference])

  const {updateCommentsPreference} = useSettingsActions()
  useEffect(() => {
    // Because the diff settings package is used from within the commit details context, we can't set the
    // preference at the point of modifying the settings data. In the future, when we unify the commit details
    // package to use the comparison viewer, this effect can be removed.
    // TOOD: once the commit details package uses the comparison viewer, we can remove this effect and move the
    // slice update to the diff-settings package, as well as within the handleSwapDiffViewCommentCollapsePreference
    // function call above.
    updateCommentsPreference(settingsData?.commentsPreference ?? CommentsPreference.Visible)
  }, [settingsData?.commentsPreference, updateCommentsPreference])

  const isFileTreeWidthPersistenceEnabled = useFeatureFlag('pull_request_file_tree_width_persistence')

  const fileTreeWidthContext = useMemo(
    () =>
      isFileTreeWidthPersistenceEnabled &&
      configOpts.useFileTree &&
      repository.ownerLogin &&
      repository.name &&
      pullRequest.number
        ? {owner: repository.ownerLogin, repo: repository.name, pullNumber: pullRequest.number}
        : undefined,
    [
      isFileTreeWidthPersistenceEnabled,
      configOpts.useFileTree,
      repository.ownerLogin,
      repository.name,
      pullRequest.number,
    ],
  )
  useFileTreeWidthSync(
    isFileTreeWidthPersistenceEnabled && configOpts.useFileTree ? user.fileTreeWidth : undefined,
    fileTreeWidthContext,
  )

  const {splitPagePaneHidden, treeViewState, treeToggleElement, collapseMobileTree, expandTree} = useTreePane(
    PR_FILE_TREE_ID,
    user.isFileTreeExpanded && !configOpts.treeCollapsedByDefault,
    configOpts.treeEnableInlineCompactMode,
    {login: user.currentUserLogin || ''},
  )

  /*
   * Diff and Filtering
   */

  // Codeowners data should be set in TSQ before filtering any diff summaries or diff entries,
  // because filtering is dependent on the codeowners data.
  const codeownersOptions: {basePath: string; initialData?: Codeowners} = {basePath: pullRequest.pathName}
  // Only set initialData if it exists
  if (codeowners) codeownersOptions.initialData = codeowners

  // TODO we can likely remove the use of `useCodeowners`. we should determine
  // if we want to use TSQ as the underlying data cache, though.
  const {data: codeownersData} = useCodeowners(codeownersOptions)
  const {setCodeowners} = useCodeownersActions()

  useEffect(() => {
    setCodeowners(codeownersData)
  }, [codeownersData, setCodeowners])

  const diffSummaryCount = useDiffSummaryCount()
  // Set value to only virtualize on the client
  const {isServer: isSSR} = useRuntimeEnvironment()
  const isDiffVirtualizationEnabled = isDiffVirtualizationFlagWithoutMinThresholdEnabled || virtualizeDiffEntries
  const diffFilterState = useDiffFilterState()
  const {updateDiffFilter} = useDiffFilterActions()

  useEffect(() => {
    updateDiffFilter({
      ...fileFilter.initialState,
      unselectedFileExtensions: new Set<string>(fileFilter.initialState.unselectedFileExtensions),
    })
  }, [updateDiffFilter, fileFilter.initialState])

  const filteredDiffSummaries = useFilteredDiffSummaries()

  // Announce filter results to screen readers when filters are interacted with
  useFilterResultsAnnouncement()

  const handleSetFilterState = useCallback(
    (updatedDiffFilterState: Partial<DiffFilterState>) => {
      updateDiffFilter(updatedDiffFilterState, true)
    },
    [updateDiffFilter],
  )

  const handleFileTreeLinkClick = useCallback(() => {
    if (treeViewState.compactMode !== 'none') {
      collapseMobileTree()
    }
  }, [collapseMobileTree, treeViewState.compactMode])

  const fileFilterRef = useRef<FileFilterHandle>(null)

  const FilterFilterComponent = useMemo(() => {
    return (
      <FileFilter
        ref={fileFilterRef}
        basePath={pullRequest.pathName}
        fileFilterMenuOptions={fileFilter.menuOptions}
        fileFilterState={diffFilterState}
        setFileFilterState={handleSetFilterState}
        viewerLogin={user.currentUserLogin}
        filterSize={treeViewState.wideMode !== 'expanded' ? 'small' : 'medium'}
      />
    )
  }, [
    pullRequest.pathName,
    fileFilter.menuOptions,
    diffFilterState,
    handleSetFilterState,
    user.currentUserLogin,
    treeViewState.wideMode,
  ])

  const canAccessChangeGroups = useCanAccessChangeGroups()
  const hasInitialDiffSummaries = initialDiffSummaries.length > 0

  const allowPullRequestGroups = configOpts.useChangeGroups && canAccessChangeGroups && hasInitialDiffSummaries
  useAutoGenerateChangeGroups({
    changeGroupsEnabled: allowPullRequestGroups,
  })

  const {aliveChannel, bannersData, pageTitle} = useRouteHeaderData()

  const handleFocusFileTreeFilter = useCallback(() => {
    expandTree()

    window.requestAnimationFrame(() => {
      fileFilterRef.current?.focus()
    })
  }, [expandTree])

  return (
    <>
      <GlobalCommands
        commands={{
          'pull-request-files-changed:collapse-expand-comments': handleSwapDiffViewCommentCollapsePreference,
          'pull-request-files-changed:focus-file-tree-filter': handleFocusFileTreeFilter,
        }}
      />
      {configOpts.useAdvancedToolbar || configOpts.useBasicToolbar ? (
        <>
          {configOpts.useAdvancedToolbar && (
            <LivePullRequestFilesToolbar
              changeGroupsPreferenceAvailable={allowPullRequestGroups}
              commits={commits}
              currentUserLogin={user.currentUserLogin}
              fileFilter={FilterFilterComponent}
              isFileTreeExpanded={treeViewState.wideMode === 'expanded'}
              lastReviewOid={user.lastReviewOid}
              pageLimits={pageLimits}
              pullRequest={pullRequest}
              repository={repository}
              shouldShowViewedFilesCount={user.shouldShowViewedFilesCount}
              totalFilesCount={diffSummaryCount}
              treeToggleElement={treeToggleElement}
              bannersData={bannersData}
              urls={urls}
              user={user}
              userNotices={userNotices}
              aliveChannel={aliveChannel}
              pageTitle={pageTitle}
            />
          )}

          {configOpts.useBasicToolbar && (
            <BasicDiffComparisonViewToolbar
              fileFilter={FilterFilterComponent}
              isFileTreeExpanded={treeViewState.wideMode === 'expanded'}
              shouldShowViewedFilesCount={user.shouldShowViewedFilesCount}
              totalFilesCount={diffSummaryCount}
              treeToggleElement={treeToggleElement}
              userNotices={userNotices}
            />
          )}

          {treeViewState.compactMode === 'overlay' && (
            <Dialog
              onClose={() => collapseMobileTree()}
              position={{narrow: 'fullscreen', regular: 'left', wide: 'left'}}
              title={`Files`}
              className="p-0"
            >
              <PullRequestFileTree
                allowPullRequestGroups={allowPullRequestGroups}
                fileFilter={FilterFilterComponent}
                filteredDiffs={filteredDiffSummaries}
                onFileSelected={handleFileTreeLinkClick}
                onSetFilterState={handleSetFilterState}
              />
            </Dialog>
          )}
        </>
      ) : null}
      {configOpts.useFileTree ? (
        <SplitPageLayout.Pane
          position="start"
          sticky
          offsetHeader={configOpts.useBasicToolbar ? '50px' : '60px'}
          aria-label="File tree"
          width={{
            min: `${MIN_WIDTH}px`,
            default: `${isFileTreeWidthPersistenceEnabled && user.fileTreeWidth ? user.fileTreeWidth : DEFAULT_WIDTH}px`,
            max: `${MAX_WIDTH}px`,
          }}
          padding="none"
          className={clsx(
            styles.Pane,
            splitPagePaneHidden && styles.HiddenPane,
            // Wide mode (container >= 1012px)
            treeViewState.wideMode === 'expanded' && styles.WideExpanded,
            treeViewState.wideMode === 'collapsed' && styles.WideCollapsed,
            // Compact mode (container < 1012px)
            treeViewState.compactMode === 'none' && styles.CompactHidden,
            treeViewState.compactMode === 'overlay' && styles.CompactOverlay,
            treeViewState.compactMode === 'inline' && styles.CompactInline,
            styles.PaneOverflowVisible,
          )}
          divider={{regular: treeViewState.compactMode === 'inline' ? 'none' : 'line', narrow: 'none'}}
          widthStorageKey={STORAGE_KEY}
          resizable={treeViewState.compactMode !== 'inline'}
        >
          <PullRequestFileTree
            allowPullRequestGroups={allowPullRequestGroups}
            className={clsx(treeStyles['sidebar'], styles.PaneContain)}
            fileFilter={FilterFilterComponent}
            filteredDiffs={filteredDiffSummaries}
            onFileSelected={handleFileTreeLinkClick}
            onSetFilterState={handleSetFilterState}
          />
        </SplitPageLayout.Pane>
      ) : null}
      <SplitPageLayout.Content
        as="div"
        width="full"
        hidden={{narrow: false, regular: treeViewState.compactMode === 'inline'}}
        padding="none"
        className={clsx(
          styles.Content,
          // Wide mode (container >= 1012px)
          treeViewState.wideMode === 'expanded' && styles.WideExpanded,
          styles.NarrowScreenContent,
        )}
      >
        {commit && (
          <PullRequestCommitHeader
            commit={commit}
            pullRequest={pullRequest}
            commits={commits}
            repository={repository}
          />
        )}
        <LargeDiffOptimizationsBanner
          isDiffVirtualizationEnabled={isDiffVirtualizationEnabled}
          isInSingleFileMode={isSingleFileMode}
          repository={repository}
          urls={urls}
        />
        <PageLimitsBanner pageLimits={pageLimits} />
        {/** Render virtualized list component and avoid single file mode if virtualization is enabled */}
        {isDiffVirtualizationEnabled && !isSSR ? (
          <PullRequestVirtualizedDiffsList
            headBranchName={pullRequest.headBranch}
            headRepoName={pullRequest.headRepositoryName}
            headRepoOwner={pullRequest.headRepositoryOwnerLogin}
            onSetFilterState={handleSetFilterState}
            pullRequestGlobalRelayId={pullRequest.globalRelayId}
            repository={repository}
            showPullRequestGroups
          />
        ) : (
          <>
            {isSingleFileMode ? (
              <SingleFileModeDiffEntryNavigator
                filteredDiffSummaries={filteredDiffSummaries}
                headBranchName={pullRequest.headBranch}
                headRepoName={pullRequest.headRepositoryName}
                headRepoOwner={pullRequest.headRepositoryOwnerLogin}
                pullRequestGlobalRelayId={pullRequest.globalRelayId}
                repository={repository}
              />
            ) : (
              <PullRequestDiffsList
                headBranchName={pullRequest.headBranch}
                headRepoName={pullRequest.headRepositoryName}
                headRepoOwner={pullRequest.headRepositoryOwnerLogin}
                onSetFilterState={handleSetFilterState}
                pullRequestGlobalRelayId={pullRequest.globalRelayId}
                repository={repository}
                showPullRequestGroups
              />
            )}
          </>
        )}
      </SplitPageLayout.Content>
    </>
  )
}

function useViewerSettings(user: FilesRoutePayload['user']) {
  const userSplitPreference = useSplitViewPreference(user.viewSettings.splitPreference)
  const userWhitespaceParam = useWhitespacePreference(user.viewSettings.hideWhitespace)
  return useMemo(() => {
    return {
      hideWhitespace: userWhitespaceParam,
      splitPreference: userSplitPreference,
      lineSpacing: user.viewSettings.lineSpacing,
      commentsPreference: user.viewSettings.commentsPreference,
    }
  }, [user.viewSettings.commentsPreference, user.viewSettings.lineSpacing, userSplitPreference, userWhitespaceParam])
}

/**
 * A layout component that provides the base structure for the diff comparison viewer.
 * It uses the SplitPageLayout component to create a two-pane layout, with the file tree on the left and the diff content on the right.

 * @param props - children: React.ReactNode - The child components to be rendered within the container.
 *                className?: string - Optional additional CSS class names to apply to the container.
 */
function Container(props: {children?: React.ReactNode; className?: string}) {
  return (
    <div id={DIFF_COMPARISON_VIEWER_CONTAINER_ID} className={clsx(styles.Container, props.className)}>
      <SplitPageLayout>{props.children}</SplitPageLayout>
    </div>
  )
}

/**
 * DiffComparisonViewer
 * A component that renders a diff comparison viewer with optional features such as file tree, commenting, and toolbars.
 *
 * Usage:
 * ```
 * <DiffComparisonViewer.Container>
 *    <DiffComparisonViewer.Viewer
 *      useCommenting
 *      useFileTree
 *      useBasicToolbar
 *      basePath={/:owner/:repo/pull/:pull_number}
 *    />
 * </DiffComparisonViewer.Container>
 * ```
 *
 * The `DiffComparisonViewer` component is composed of two main parts:
 * 1. `DiffComparisonViewer.Container`: This is a layout component that provides the overall structure for the diff comparison viewer. It uses the `SplitPageLayout` component to create a two-pane layout, with the file tree on the left and the diff content on the right.
 * 2. `DiffComparisonViewer.Viewer`: This is the main component that handles the rendering of the diff comparison viewer. It takes various props to customize its behavior, such as enabling commenting, file tree, and toolbars. It also manages the state of the viewer, including fetching initial data if necessary.
 *
 * The `DiffComparisonViewer` also exposes a `Header` component that can be used to render a header above the viewer, typically for displaying pull request information.
 * This is just a re-export of `SplitPageLayout.Header`.
 */
export const DiffComparisonViewer = Object.assign(
  {},
  {
    Container,
    Header: SplitPageLayout.Header,
    Viewer: BaseDiffComparisonViewer,
  },
)
